import { useMemo, useState } from "react";
import { BookOpen } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import useBlogPosts from "@/hooks/useBlogPosts";
import ContourBackground from "@/components/ContourBackground";
import PostCard from "@/components/blog/PostCard";
import { breadcrumb } from "@/lib/jsonLd";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function CategoryFilter({ categories, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="blog-filters">
      {["All", ...categories].map((c) => {
        const on = active === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            data-testid={`blog-filter-${slug(c)}`}
            className={`mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border transition-colors ${
              on ? "bg-topo border-topo text-white" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
            }`}
          >
            {c}
          </button>
        );
      })}
    </div>
  );
}

export default function Blog() {
  useSeo({
    title: "CAD Drafting Guides & Buyer Questions — Tri-Axis XYZ Blog",
    description:
      "Straight answers to the questions survey firms, architects, contractors and engineers ask before outsourcing CAD drafting, BIM, MEP, piping and steel detailing.",
  });
  useJsonLd(breadcrumb([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]), "blog-bc");

  const { posts, loading, error } = useBlogPosts();
  const [active, setActive] = useState("All");
  const categories = useMemo(() => [...new Set(posts.map((p) => p.category))], [posts]);
  const visible = active === "All" ? posts : posts.filter((p) => p.category === active);
  const [lead, ...rest] = visible;

  return (
    <div data-testid="page-blog">
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.45} seed={41} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo inline-flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> Insights · Buyer guides
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 max-w-4xl">
            Straight answers to the drafting questions <span className="text-orange-survey">buyers actually ask.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Pricing, turnaround, security, standards and deliverables for land-survey CAD, BIM, MEP, plant piping,
            steel detailing and permit drafting, written by the people who produce the drawings.
          </p>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <CategoryFilter categories={categories} active={active} onChange={setActive} />
          {loading && <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="h-60 bg-paper-dark/40 animate-pulse" />)}</div>}
          {error && <p className="mt-10 text-slate-600" data-testid="blog-error">Articles couldn't load right now. Please refresh in a moment.</p>}
          {!loading && !error && visible.length === 0 && <p className="mt-10 text-slate-600" data-testid="blog-empty">No articles in this category yet.</p>}
          {lead && (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              <div className="lg:col-span-2 lg:row-span-2 grid"><PostCard post={lead} featured /></div>
              {rest.map((p) => <PostCard key={p.slug} post={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
